import React, { useState } from 'react';
import Accordion from '../../../../Accordion';
import { Info, Database, Calculator, TrendingUp, BarChart3 } from 'lucide-react';

/**
 * Data Sources & Methodology card for Demand and Fuel Price Evolution (AR6 scenario chart).
 * Responsive layout aligned with Overview documentation accordion (overview-tab-mobile-responsive-changes.md §5).
 */
const DemandFuelPriceDocumentationAccordion = () => {
  const [activeKey, setActiveKey] = useState(null);

  return (
    <div className="mt-6 w-full min-w-0 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 md:px-6 lg:px-8 py-4 md:py-5 border-b border-gray-200">
        <div className="flex items-center gap-3 md:gap-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Info className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-gray-700">
              Data Sources & Methodology
            </h3>
            <p className="text-sm md:text-base text-gray-500 mt-0.5">
              Future projections of demand, fuel prices, and electricity mix under different climate scenarios
            </p>
          </div>
        </div>
      </div>

      <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)} className="border-0">
        <Accordion.Item eventKey="0" className="border-0 border-b border-gray-100">
          <Accordion.Header className="px-4 md:px-6 lg:px-8 py-4 md:py-5 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3 w-full">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="h-4 w-4 text-orange-600" />
              </div>
              <div className="text-left">
                <h4 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 mb-0">All Projections</h4>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5 mb-0">
                  CO₂ Price, Electricity Demand, Hydrogen Demand, Fuel Prices, Electricity Share
                </p>
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body className="px-4 md:px-6 lg:px-8 pb-4 md:pb-5">
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Database className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Data Source</p>
                  <p className="text-xs lg:text-sm text-gray-700">IPCC AR6 climate scenario projections</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calculator className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Processing</p>
                  <p className="text-xs lg:text-sm text-gray-700">None. Direct scenario data visualization.</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Note</p>
                  <p className="text-xs lg:text-sm text-gray-700 mb-2">
                    C1-C7 represent different levels of climate action ambition:
                  </p>
                  <ul className="text-xs lg:text-sm text-gray-700 space-y-1 ml-4 list-disc">
                    <li><strong>C1:</strong> Most ambitious (rapid decarbonization, strong climate policy)</li>
                    <li><strong>C2-C3:</strong> High ambition</li>
                    <li><strong>C4:</strong> Moderate ambition</li>
                    <li><strong>C7:</strong> Limited action (current policies continue)</li>
                  </ul>
                  <p className="text-xs lg:text-sm text-gray-700 mt-2">
                    Each scenario projects different trajectories for carbon prices, energy demand, fuel costs, and
                    generation mix based on policy assumptions.
                  </p>
                </div>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1" className="border-0">
          <Accordion.Header className="px-4 md:px-6 lg:px-8 py-4 md:py-5 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3 w-full">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="h-4 w-4 text-green-600" />
              </div>
              <div className="text-left">
                <h4 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 mb-0">
                  Electricity Demand Projections
                </h4>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5 mb-0">Additional context on historical baseline</p>
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body className="px-4 md:px-6 lg:px-8 pb-4 md:pb-5">
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Database className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Historical Baseline</p>
                  <p className="text-xs lg:text-sm text-gray-700">IEA (International Energy Agency)</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Note</p>
                  <p className="text-xs lg:text-sm text-gray-700">
                    Historical IEA data is shown to validate that AR6 projections follow observed trends before
                    diverging into scenario-specific pathways. This continuity from historical data adds confidence to the
                    projection methodology.
                  </p>
                </div>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
};

export default DemandFuelPriceDocumentationAccordion;
